import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { TablePagination } from '@/components/ui/table-pagination'
import {
  BlocklistDetailDialog,
  type BlocklistEntry,
  BLOCK_TYPE_META,
  getBlockTypeMeta,
  isCrmSourced,
  getDisplayName,
} from '@/components/master-inbox/BlocklistDetailDialog'
import { supabase } from '@/integrations/supabase/client'
import { useDashboard } from '@/contexts/DashboardContext'
import { useClients } from '@/hooks/useClients'
import { toast } from '@/hooks/useToastHistory'
import {
  ShieldBan, Search, RefreshCw, Download, Unlock, Loader2,
  Mail, Globe, Clock, AlertTriangle, Database, MessageSquare
} from 'lucide-react'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog'

// PostgREST filter-value escape: commas, parens, and backslashes have special meaning inside .or()
function escapeFilterValue(s: string): string {
  return s.replace(/[\\(),]/g, '\\$&')
}

// Apply all user-selected filters to a Supabase query builder. Used both for paged data
// and for CSV export (same filter semantics; different select/pagination).
function applyBlocklistFilters<T>(
  query: T,
  opts: {
    selectedClientName: string | null
    statusFilter: 'all' | 'active' | 'expired'
    sourceFilter: 'all' | 'reply' | 'crm'
    blockTypeFilter: string
    categoryFilter: 'all' | 'positive' | 'negative'
    search: string
  }
): T {
  let q: any = query
  if (opts.selectedClientName) q = q.ilike('workspace_name', opts.selectedClientName)
  if (opts.statusFilter === 'active') q = q.eq('expired', false)
  else if (opts.statusFilter === 'expired') q = q.eq('expired', true)
  if (opts.sourceFilter === 'reply') q = q.is('external_source', null)
  else if (opts.sourceFilter === 'crm') q = q.not('external_source', 'is', null)
  if (opts.blockTypeFilter !== 'all') q = q.eq('block_type', opts.blockTypeFilter)
  if (opts.categoryFilter !== 'all') q = q.eq('reply_category_type', opts.categoryFilter)
  const s = opts.search.trim()
  if (s) {
    const v = escapeFilterValue(s)
    q = q.or(
      [
        `email.ilike.%${v}%`,
        `domain.ilike.%${v}%`,
        `name.ilike.%${v}%`,
        `campaign_name.ilike.%${v}%`,
        `workspace_name.ilike.%${v}%`,
        `label_name.ilike.%${v}%`,
        `job_title.ilike.%${v}%`,
        `external_source.ilike.%${v}%`,
        `external_metadata->>account_name.ilike.%${v}%`,
      ].join(',')
    )
  }
  return q as T
}

interface BlocklistStats {
  total: number
  active: number
  expired: number
  replyActive: number
  crmActive: number
  permanent: number
  expiring: number
}

export function MasterInboxBlocklistTab() {
  // State
  const [pageEntries, setPageEntries] = useState<BlocklistEntry[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [stats, setStats] = useState<BlocklistStats>({
    total: 0, active: 0, expired: 0, replyActive: 0, crmActive: 0, permanent: 0, expiring: 0
  })
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [blockTypeFilter, setBlockTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired'>('all')
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'positive' | 'negative'>('all')
  const [sourceFilter, setSourceFilter] = useState<'all' | 'reply' | 'crm'>('all')
  const [selectedEntry, setSelectedEntry] = useState<BlocklistEntry | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [unblockingId, setUnblockingId] = useState<string | null>(null)
  const [confirmUnblock, setConfirmUnblock] = useState<BlocklistEntry | null>(null)
  const [exporting, setExporting] = useState(false)

  // Context
  const { state } = useDashboard()
  const { data: clients = [] } = useClients()

  const selectedClientName = useMemo(() => {
    if (state.selectedClient === 'all') return null
    const client = clients.find(c => c.id === state.selectedClient)
    return client?.name || null
  }, [state.selectedClient, clients])

  // Debounce search input (300ms) so we don't hammer PostgREST on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(t)
  }, [searchTerm])

  // Reset to page 1 whenever a filter or search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, blockTypeFilter, statusFilter, categoryFilter, sourceFilter, itemsPerPage, selectedClientName])

  // Fetch the current page of rows with all filters applied server-side.
  const fetchPage = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const from = (currentPage - 1) * itemsPerPage
      const to = from + itemsPerPage - 1
      let q: any = (supabase as any)
        .from('blocklist')
        .select('*', { count: 'exact' })
      q = applyBlocklistFilters(q, {
        selectedClientName,
        statusFilter,
        sourceFilter,
        blockTypeFilter,
        categoryFilter,
        search: debouncedSearch,
      })
      const { data, count, error: fetchError } = await q
        .order('created_at', { ascending: false })
        .range(from, to)

      if (fetchError) throw new Error(fetchError.message)
      setPageEntries(data || [])
      setTotalCount(count ?? 0)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
      console.error('Error fetching blocklist entries:', err)
      setPageEntries([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [
    currentPage, itemsPerPage, selectedClientName,
    statusFilter, sourceFilter, blockTypeFilter, categoryFilter, debouncedSearch,
  ])

  // Fetch KPI stats independent of filters (scoped only by selected client).
  // Filters only affect the table; stats should always show the big picture.
  const fetchStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const base = () => {
        let q: any = (supabase as any).from('blocklist').select('id', { count: 'exact', head: true })
        if (selectedClientName) q = q.ilike('workspace_name', selectedClientName)
        return q
      }
      const [totalRes, activeRes, replyActiveRes, crmActiveRes, expiringRes] = await Promise.all([
        base(),
        base().eq('expired', false),
        base().eq('expired', false).is('external_source', null),
        base().eq('expired', false).not('external_source', 'is', null),
        base().eq('expired', false).not('expires_at', 'is', null),
      ])
      const total = totalRes.count ?? 0
      const active = activeRes.count ?? 0
      const replyActive = replyActiveRes.count ?? 0
      const crmActive = crmActiveRes.count ?? 0
      const expiring = expiringRes.count ?? 0
      setStats({
        total,
        active,
        expired: total - active,
        replyActive,
        crmActive,
        permanent: active - expiring,
        expiring,
      })
    } catch (err) {
      console.error('Error fetching blocklist stats:', err)
    } finally {
      setStatsLoading(false)
    }
  }, [selectedClientName])

  useEffect(() => { fetchPage() }, [fetchPage])
  useEffect(() => { fetchStats() }, [fetchStats])

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage))

  // Row click
  const handleRowClick = (entry: BlocklistEntry) => {
    setSelectedEntry(entry)
    setDetailsOpen(true)
  }

  // Unblock
  const handleUnblock = async (entry: BlocklistEntry) => {
    setUnblockingId(entry.id)
    try {
      // Call an edge function to handle SmartLead API + DB update
      const { data, error: fnError } = await supabase.functions.invoke('process-blocklist-expiry', {
        body: {
          manualUnblock: true,
          entryId: entry.id
        }
      })

      if (fnError) throw fnError

      if (data?.success) {
        const label = entry.email || entry.domain || getDisplayName(entry)
        toast({
          title: isCrmSourced(entry) ? 'Domain Unblocked' : 'Lead Unblocked',
          description: `${label} has been removed from the blocklist and SmartLead.`,
        })
        // Refresh page + stats
        await Promise.all([fetchPage(), fetchStats()])
      } else {
        throw new Error(data?.error || 'Failed to unblock')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      console.error('Unblock error:', err)
      toast({
        title: 'Unblock Failed',
        description: msg,
        variant: 'destructive',
      })
    } finally {
      setUnblockingId(null)
      setConfirmUnblock(null)
    }
  }

  // CSV Export — fetches ALL matching rows (not just the current page) with the same
  // server-side filters. Paginates in 1000-row chunks to avoid overwhelming PostgREST.
  const handleExportCSV = async () => {
    if (totalCount === 0) {
      toast({ title: 'No Data', description: 'No entries to export.' })
      return
    }
    setExporting(true)
    try {
      const CHUNK = 1000
      const allRows: BlocklistEntry[] = []
      let from = 0
      // Guard against runaway fetches; 50k is a generous ceiling for CSV export.
      const MAX_ROWS = 50000
      while (from < Math.min(totalCount, MAX_ROWS)) {
        const to = from + CHUNK - 1
        let q: any = (supabase as any).from('blocklist').select('*')
        q = applyBlocklistFilters(q, {
          selectedClientName,
          statusFilter,
          sourceFilter,
          blockTypeFilter,
          categoryFilter,
          search: debouncedSearch,
        })
        const { data, error: fetchError } = await q
          .order('created_at', { ascending: false })
          .range(from, to)
        if (fetchError) throw new Error(fetchError.message)
        const chunk = (data || []) as BlocklistEntry[]
        allRows.push(...chunk)
        if (chunk.length < CHUNK) break
        from += CHUNK
      }

      if (allRows.length === 0) {
        toast({ title: 'No Data', description: 'No entries to export.' })
        return
      }

      const headers = [
        'Email', 'Domain', 'Name', 'Job Title', 'LinkedIn',
        'Label', 'Category', 'Block Type', 'Block Reason',
        'Expiry', 'Status', 'SmartLead Sync',
        'Campaign', 'Workspace', 'Reply Count',
        'Blocked At', 'Reply Subject',
        'Source', 'External Source', 'External ID', 'External URL', 'External Metadata'
      ]

      const rows = allRows.map(e => [
        e.email || '',
        e.domain || '',
        getDisplayName(e),
        e.job_title || '',
        e.linkedin || '',
        e.label_name || '',
        e.reply_category_type || '',
        e.block_type || '',
        e.block_reason || '',
        e.expires_at ? new Date(e.expires_at).toLocaleDateString() : 'Permanent',
        e.expired ? 'Expired' : 'Active',
        e.smartlead_sync_status || '',
        e.campaign_name || '',
        e.workspace_name || '',
        e.reply_count != null ? String(e.reply_count) : '',
        e.created_at ? new Date(e.created_at).toLocaleString() : '',
        e.reply_subject || '',
        isCrmSourced(e) ? 'CRM' : 'Reply',
        e.external_source || '',
        e.external_id || '',
        e.external_url || '',
        e.external_metadata ? JSON.stringify(e.external_metadata) : ''
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row =>
          row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        )
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `blocklist-export-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      const capped = allRows.length >= MAX_ROWS && totalCount > MAX_ROWS
      toast({
        title: 'Export Complete',
        description: capped
          ? `Exported first ${allRows.length.toLocaleString()} of ${totalCount.toLocaleString()} entries (cap reached).`
          : `Exported ${allRows.length.toLocaleString()} entries to CSV.`,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      console.error('Export error:', err)
      toast({ title: 'Export Failed', description: msg, variant: 'destructive' })
    } finally {
      setExporting(false)
    }
  }

  const getCategoryColor = (type: string | null) => {
    if (!type) return ''
    const t = type.toLowerCase()
    if (t === 'positive') return 'bg-green-100 text-green-800 border-green-300'
    if (t === 'negative') return 'bg-red-100 text-red-800 border-red-300'
    return 'bg-gray-100 text-gray-800 border-gray-300'
  }

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      })
    } catch {
      return dateString
    }
  }

  return (
    <>
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldBan className="w-5 h-5" />
                SmartLead Global Blocklist
              </CardTitle>
              <CardDescription>
                Leads and domains blocked from future outreach in SmartLead. Includes reply-driven blocks (positive adds email + domain, negative adds email only) and CRM-sourced blocks (current/former customers, opportunities, meetings, other divisions, explicit blacklist).
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={exporting || loading}>
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {exporting ? 'Exporting…' : 'Export CSV'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { fetchPage(); fetchStats() }}
                disabled={loading || statsLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* KPI Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <div className="bg-slate-50 border rounded-lg p-3">
              <div className="text-xs text-muted-foreground">Active Blocks</div>
              <div className="text-xl font-bold mt-1">{stats.active}</div>
              <div className="text-xs text-muted-foreground">{stats.replyActive} reply-driven, {stats.crmActive} CRM-sourced</div>
            </div>
            <div className="bg-slate-50 border rounded-lg p-3">
              <div className="text-xs text-muted-foreground">Permanent</div>
              <div className="text-xl font-bold mt-1">{stats.permanent}</div>
              <div className="text-xs text-muted-foreground">Never expires</div>
            </div>
            <div className="bg-slate-50 border rounded-lg p-3">
              <div className="text-xs text-muted-foreground">Expiring</div>
              <div className="text-xl font-bold mt-1">{stats.expiring}</div>
              <div className="text-xs text-muted-foreground">Auto-removes on expiry date</div>
            </div>
            <div className="bg-slate-50 border rounded-lg p-3">
              <div className="text-xs text-muted-foreground">Expired / Removed</div>
              <div className="text-xl font-bold mt-1">{stats.expired}</div>
              <div className="text-xs text-muted-foreground">Already unblocked</div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex items-center gap-3 pt-4 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <Input
                placeholder="Search by email, name, domain, campaign, label..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
                className="flex-1"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as typeof statusFilter); setCurrentPage(1) }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v as typeof sourceFilter); setCurrentPage(1) }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="reply">Reply-driven</SelectItem>
                <SelectItem value="crm">CRM-sourced</SelectItem>
              </SelectContent>
            </Select>
            <Select value={blockTypeFilter} onValueChange={(v) => { setBlockTypeFilter(v); setCurrentPage(1) }}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Block Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Block Types</SelectItem>
                {Object.entries(BLOCK_TYPE_META).map(([key, meta]) => (
                  <SelectItem key={key} value={key}>
                    {meta.icon} {meta.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v as typeof categoryFilter); setCurrentPage(1) }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="positive">Positive</SelectItem>
                <SelectItem value="negative">Negative</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col min-h-0">
          {error && (
            <div className="text-destructive text-sm mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </div>
          )}

          {loading && pageEntries.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : pageEntries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <ShieldBan className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No blocklist entries found</p>
              <p className="text-sm">
                {searchTerm ? 'Try adjusting your search terms' : 'No leads have been blocked yet'}
              </p>
            </div>
          ) : (
            <>
              <div className="flex-1 w-full overflow-hidden">
                <div className="w-full overflow-x-auto">
                  <Table className="w-full table-fixed">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[20%]">Lead / Account</TableHead>
                        <TableHead className="w-[8%]">Source</TableHead>
                        <TableHead className="w-[12%]">Label</TableHead>
                        <TableHead className="w-[15%]">Block Type</TableHead>
                        <TableHead className="w-[9%]">Expiry</TableHead>
                        <TableHead className="w-[5%]">Sync</TableHead>
                        <TableHead className="w-[15%]">Campaign / Origin</TableHead>
                        <TableHead className="w-[9%]">Blocked At</TableHead>
                        <TableHead className="w-[7%]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pageEntries.map((entry) => {
                        const fromCrm = isCrmSourced(entry)
                        const displayName = getDisplayName(entry)
                        const blockTypeMeta = getBlockTypeMeta(entry.block_type)
                        return (
                        <TableRow
                          key={entry.id}
                          className={`cursor-pointer hover:bg-muted/40 ${entry.expired ? 'opacity-60' : ''}`}
                          onClick={() => handleRowClick(entry)}
                        >
                          <TableCell>
                            <div className="flex flex-col gap-0.5 min-w-0">
                              <span className="font-medium text-sm truncate">{displayName}</span>
                              <span className="text-xs text-muted-foreground truncate">
                                {entry.email || entry.domain || <span className="italic">—</span>}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {fromCrm ? (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 whitespace-nowrap">
                                <Database className="w-3 h-3 mr-1" />
                                CRM
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 whitespace-nowrap">
                                <MessageSquare className="w-3 h-3 mr-1" />
                                Reply
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1 min-w-0">
                              {entry.label_name ? (
                                <Badge variant="outline" className="text-xs whitespace-nowrap w-fit">
                                  {entry.label_name}
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                              {entry.reply_category_type && (
                                <Badge variant="outline" className={`text-xs w-fit ${getCategoryColor(entry.reply_category_type)}`}>
                                  {entry.reply_category_type}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs whitespace-nowrap ${blockTypeMeta.color}`}>
                              <span className="mr-1">{blockTypeMeta.icon}</span>
                              {blockTypeMeta.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {entry.expired ? (
                              <Badge variant="outline" className="text-xs bg-gray-50 text-gray-600 border-gray-200 whitespace-nowrap">
                                Expired
                              </Badge>
                            ) : entry.expires_at ? (
                              <span className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(entry.expires_at)}
                              </span>
                            ) : (
                              <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200 whitespace-nowrap">
                                Permanent
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={`text-xs font-medium ${
                              entry.smartlead_sync_status === 'success' ? 'text-green-600' :
                              entry.smartlead_sync_status === 'failed' ? 'text-red-600' :
                              'text-gray-500'
                            }`}>
                              {entry.smartlead_sync_status === 'success' ? '✅' :
                               entry.smartlead_sync_status === 'failed' ? '❌' :
                               entry.smartlead_sync_status === 'already_blocked' ? '⏭️' :
                               '⏳'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-muted-foreground truncate block">
                              {fromCrm
                                ? (entry.external_source ? `${entry.external_source} • ${(entry.external_metadata?.account_name as string) || '—'}` : '—')
                                : (entry.campaign_name || '—')}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(entry.created_at)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {!entry.expired && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setConfirmUnblock(entry)
                                }}
                                disabled={unblockingId === entry.id}
                              >
                                {unblockingId === entry.id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Unlock className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Pagination */}
              <div className="mt-4 border-t pt-4">
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalCount}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1) }}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <BlocklistDetailDialog
        entry={selectedEntry}
        isOpen={detailsOpen}
        onClose={() => {
          setDetailsOpen(false)
          setSelectedEntry(null)
        }}
      />

      {/* Unblock Confirmation */}
      <AlertDialog open={!!confirmUnblock} onOpenChange={(open) => !open && setConfirmUnblock(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmUnblock && isCrmSourced(confirmUnblock) ? 'Unblock Domain' : 'Unblock Lead'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unblock{' '}
              <strong>
                {confirmUnblock?.email || confirmUnblock?.domain || (confirmUnblock ? getDisplayName(confirmUnblock) : '')}
              </strong>?
              {confirmUnblock?.block_type === 'email_and_domain' && confirmUnblock?.domain && (
                <> This will also unblock the domain <strong>{confirmUnblock.domain}</strong>.</>
              )}
              <br /><br />
              This will remove it from the SmartLead global blocklist, allowing future outreach campaigns to target this {confirmUnblock && isCrmSourced(confirmUnblock) ? 'domain' : 'lead'} again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmUnblock && handleUnblock(confirmUnblock)}
              className="bg-red-600 hover:bg-red-700"
            >
              {unblockingId === confirmUnblock?.id ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Unblocking...
                </>
              ) : (
                <>
                  <Unlock className="w-4 h-4 mr-2" />
                  Unblock
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
